<script setup>
// InteliChat #41 - new-chat toast/banner. On phones this is the tappable
// top banner ("New chat - Tap to open"); on desktop it is a small corner
// fallback so an arrival is seen even with the rail collapsed. Driven by
// the SAME mitt bus the engine + HUD use (one stream, not a poll).
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';

const route = useRoute();
const router = useRouter();

const visible = ref(false);
const toast = ref({ id: null, name: 'Customer', lastLine: '' });
let hideTimer = null;

const MESSAGE_TYPE_INCOMING = 0;

const currentlyOpen = id => {
  const openId = Number(route.params?.conversationId);
  return (
    openId &&
    Number(openId) === Number(id) &&
    document.visibilityState === 'visible'
  );
};

const show = ({ id, name, lastLine }) => {
  if (!id || currentlyOpen(id)) return;
  toast.value = { id, name: name || 'Customer', lastLine: lastLine || 'New message' };
  visible.value = true;
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    visible.value = false;
  }, 5000);
};

const onNewConversation = payload => {
  show({
    id: payload?.id,
    name: payload?.meta?.sender?.name,
    lastLine: 'started a new chat',
  });
};

const onNewMessage = payload => {
  const type = payload?.message_type;
  if (type !== undefined && type !== MESSAGE_TYPE_INCOMING) return;
  show({
    id: payload?.conversation_id ?? payload?.conversation?.id,
    name: payload?.sender?.name,
    lastLine: (payload?.content || 'sent a message').toString().slice(0, 60),
  });
};

const openIt = () => {
  const id = toast.value.id;
  visible.value = false;
  if (!id) return;
  router.push({
    name: 'intelichat_inbox',
    params: { accountId: route.params.accountId, conversationId: id },
  });
};

const dismiss = () => {
  visible.value = false;
};

// light swipe-UP to dismiss on phones (tap-to-open + the x also work);
// keeps the toast non-intrusive and easy to flick away.
let touchStartY = null;
const onTouchStart = e => {
  touchStartY = e.touches?.[0]?.clientY ?? null;
};
const onTouchMove = e => {
  if (touchStartY == null) return;
  const dy = touchStartY - (e.touches?.[0]?.clientY ?? touchStartY);
  if (dy > 36) {
    dismiss();
    touchStartY = null;
  }
};
const onTouchEnd = () => {
  touchStartY = null;
};

onMounted(() => {
  emitter.on('intelichat:new_conversation', onNewConversation);
  emitter.on('intelichat:new_message', onNewMessage);
});
onBeforeUnmount(() => {
  emitter.off('intelichat:new_conversation', onNewConversation);
  emitter.off('intelichat:new_message', onNewMessage);
  if (hideTimer) clearTimeout(hideTimer);
});
</script>

<template>
  <transition name="ic41-toast">
    <div
      v-if="visible"
      class="ic41-toast"
      role="status"
      @click="openIt"
      @touchstart.passive="onTouchStart"
      @touchmove.passive="onTouchMove"
      @touchend="onTouchEnd"
    >
      <span class="ic41-toast__pip" aria-hidden="true" />
      <span class="ic41-toast__body">
        <span class="ic41-toast__t">New chat - {{ toast.name }}</span>
        <span class="ic41-toast__s">{{ toast.lastLine }} - Tap to open</span>
      </span>
      <button
        class="ic41-toast__x"
        type="button"
        aria-label="Dismiss"
        @click.stop="dismiss"
      >×</button>
    </div>
  </transition>
</template>

<style scoped lang="scss">
.ic41-toast {
  position: fixed;
  z-index: 90;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  background: var(--il-card, #fff);
  border: 1px solid var(--il-accent-line, #e4d4f7);
  border-left: 4px solid var(--il-accent-2, #ec4899);
  border-radius: 12px;
  box-shadow: var(--il-sh-lg, 0 24px 60px -12px rgba(22, 26, 44, 0.26));
  padding: 10px 12px;
  font-family: var(--il-f, system-ui, sans-serif);

  /* desktop: bottom-LEFT corner (clear of the right-edge Intel Center rail
     so it never overlaps the HUD) */
  left: 20px;
  bottom: 20px;
  width: 320px;
  max-width: calc(100vw - 32px);
}

.ic41-toast__pip {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--il-accent-2, #ec4899);
  animation: ic41-toast-pip 1.1s ease-in-out infinite;
}

.ic41-toast__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.ic41-toast__t {
  font-weight: 700;
  font-size: 13px;
  color: var(--il-ink, #161a2c);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ic41-toast__s {
  font-size: 12px;
  color: var(--il-ink-3a, #6b7290);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ic41-toast__x {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  color: var(--il-ink-3, #828aa8);
  cursor: pointer;
  padding: 0 2px;
}

/* phones: full-width top banner, offset BELOW the workspace header so it
   does not obscure the page title */
@media (max-width: 767.98px) {
  .ic41-toast {
    top: calc(56px + env(safe-area-inset-top, 0px));
    left: 10px;
    right: 10px;
    bottom: auto;
    width: auto;
  }
}

.ic41-toast-enter-active,
.ic41-toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.ic41-toast-enter-from,
.ic41-toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes ic41-toast-pip {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ic41-toast__pip {
    animation: none;
  }
  .ic41-toast-enter-active,
  .ic41-toast-leave-active {
    transition: opacity 0.22s ease;
  }
  .ic41-toast-enter-from,
  .ic41-toast-leave-to {
    transform: none;
  }
}
</style>
